import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import groupBy from 'lodash/groupBy';
import styled from 'styled-components';
import { Button, Link, LoaderSpin, ModalFooter, ModalLayout } from 'components';
import { appConfig } from 'config/app';
import { theme, toRem } from 'design';
import { AccountHealth, IAccountHealthModalConfig } from 'modals/AccountHealth';
import { routeConstants } from 'routes';
import { browserHistory } from 'routes/Router';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { IStore } from 'store/types';
import { getUserPendingPayoutsRequest, User } from 'store/User';
import { PayoutApprovalStatus } from 'types';

const LoadingLayout = styled(ModalLayout)`
  align-items: center;
  justify-content: center;
  min-height: ${toRem(200)};
  padding: ${toRem(theme.space['3xl'])} ${toRem(theme.space.lg)};
`;

export const PENDING_PAYMENTS_MODAL_CONFIG: IAccountHealthModalConfig = {
  modalTitle: 'Pending Payments',
  modalSubtitle: 'Instant payments we are unable to deliver.',
  helpListTitle: 'Please confirm the following',
  helpListItems: [
    'A valid, reloadable Visa or Mastercard debit card is on file',
    'The card has not expired, and the expiration date in Kickfin matches the date printed on the card',
    'If this is a new card, make sure you have followed the instructions to activate the card',
    'The card’s balance is below any maximum balance limit',
    'No bank security blocks are preventing deposits',
    <>
      For small payments, your card may have a minimum reload requirement.{' '}
      <Link as="a" href={appConfig.LINKS.SUPPORT} target="_blank" rel="noopener noreferrer" data-cy="pending-payments-support-link">
        Contact Kickfin Support
      </Link>{' '}
      if needed
    </>,
  ],
};

export const IN_REVIEW_PAYMENTS_MODAL_CONFIG: IAccountHealthModalConfig = {
  modalTitle: 'Payments In Review',
  modalSubtitle: 'Payments that require manager approval',
  helpListTitle: 'Please contact your manager',
  helpListItems: ['The payments listed below require manager approval. Please contact your manager and request that the payments be approved.'],
  hideHelpListBullets: true,
};

interface PendingPaymentsProps {
  isActive: boolean;
  onClose: () => void;
  siteId?: string;
}

const PendingPayments = ({ isActive, onClose, siteId }: PendingPaymentsProps) => {
  const dispatch = useDispatch();
  const pendingPayouts = useSelector((state: IStore) => state.user?.pendingPayouts?.data || []);
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, User.GET_USER_PENDING_PAYOUTS_REQUEST));
  const groupedPayouts = useMemo(() => groupBy(pendingPayouts, (payout) => payout.site.id), [pendingPayouts]);
  // Show the 'In Review' modal only when every payout is pending for manager approval; any other delivery issue falls back to the standard modal since no payouts can be delivered until that issue is resolved.
  const allInReview = useMemo(() => pendingPayouts.length > 0 && pendingPayouts.every((payout) => payout.approvalCheck?.status === PayoutApprovalStatus.REVIEW_REQUIRED), [pendingPayouts]);
  // We want to show the loading state until we've dispatched the request for pending payouts and received a response, to avoid briefly showing the standard modal with no payouts listed while the request is in-flight.
  const hasDispatched = useRef(false);

  useEffect(() => {
    dispatch(getUserPendingPayoutsRequest(siteId));
    hasDispatched.current = true;
  }, [dispatch, siteId]);

  if (isLoading || !hasDispatched.current) {
    return (
      <LoadingLayout isActive={isActive} onClose={onClose} data-cy="pending-payments-loading-modal">
        <LoaderSpin color={theme.color.neutral[200]} />
      </LoadingLayout>
    );
  }

  if (allInReview) {
    return <AccountHealth isActive={isActive} onClose={onClose} dataCy="pending-payments-in-review" config={IN_REVIEW_PAYMENTS_MODAL_CONFIG} payouts={groupedPayouts} />;
  }

  const modalFooter = (
    <ModalFooter columns={2}>
      <Button label="Close" onClick={onClose} appearance="white" data-cy="pending-payments-close-button" />
      <Button label="Update card" onClick={() => browserHistory.push(routeConstants.private.settingsPaymentMethod)} data-cy="pending-payments-update-card-button" />
    </ModalFooter>
  );

  return <AccountHealth isActive={isActive} onClose={onClose} dataCy="pending-payments" config={{ ...PENDING_PAYMENTS_MODAL_CONFIG, modalFooter }} payouts={groupedPayouts} />;
};

export default PendingPayments;
